import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminScheduleEvent, AdminGroup } from '@/lib/api'
import RowActions from '@/components/RowActions'
import { useConfirm } from '@/components/ConfirmDialog'

export default function SchedulePage() {
  const { token, isGroupManager, managedGroupSlug } = useAuth()
  const navigate = useNavigate()

  const [items, setItems] = useState<AdminScheduleEvent[]>([])
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [filterGroup, setFilterGroup] = useState(
    () => (isGroupManager && managedGroupSlug) ? managedGroupSlug : ''
  )
  const [filterDate, setFilterDate] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { confirm, dialog } = useConfirm()

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const [events, groupList] = await Promise.all([
        api.getAdminSchedule(token),
        isGroupManager ? Promise.resolve([]) : api.getAdminGroups(token),
      ])
      setItems(events)
      setGroups(groupList)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [token, isGroupManager])

  useEffect(() => { load() }, [load])

  const handleDelete = async (id: string, name: string) => {
    if (!await confirm(`"${name}" akan dihapus permanen dan tidak bisa dipulihkan.`, { title: `Hapus "${name}"` })) return
    if (!token) return
    try {
      await api.deleteSchedule(token, id)
      load()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  const filtered = items
    .filter((e) => (!filterGroup || e.group_slug === filterGroup) && (!filterDate || e.date === filterDate))
    .sort((a, b) => {
      if (!a.date && !b.date) return 0
      if (!a.date) return 1
      if (!b.date) return -1
      return new Date(b.date).getTime() - new Date(a.date).getTime()
    })

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-white">Schedule</h1>
          <p className="text-zinc-500 text-xs mt-0.5">{filtered.length} events</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="bg-zinc-800 text-white text-xs rounded px-3 py-2 border border-zinc-700 focus:outline-none focus:border-zinc-500"
          />
          {!isGroupManager && (
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              className="bg-zinc-800 text-white text-xs rounded px-3 py-2 border border-zinc-700 focus:outline-none"
            >
              <option value="">All groups</option>
              {groups.map((g) => (
                <option key={g.id} value={g.slug}>{g.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => navigate('/schedule/create')}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            + Add Event
          </button>
        </div>
      </div>

      {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
      {error && <p className="text-red-400 text-sm">{error}</p>}

      {!loading && !error && filtered.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-zinc-500 text-sm">No events yet.</p>
          <button
            onClick={() => navigate('/schedule/create')}
            className="mt-3 text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            + Add the first one
          </button>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-zinc-800">
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Date</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Name</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Group</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Location</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Time</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Badge</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {filtered.map((event) => (
                <tr
                  key={event.id}
                  onClick={() => navigate(`/schedule/${event.id}`, { state: { event } })}
                  className="hover:bg-zinc-800/50 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{event.date ?? <span className="text-zinc-600 italic">TBA</span>}</td>
                  <td className="px-4 py-3 text-white font-medium">{event.name}</td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{event.group_slug}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{event.location}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{event.time}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] tracking-wide px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">
                      {event.badge}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <RowActions actions={[
                      { label: 'Edit', onClick: () => navigate(`/schedule/${event.id}/edit`, { state: { event } }) },
                      { label: 'Hapus', onClick: () => handleDelete(event.id, event.name), variant: 'danger' },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog}
    </div>
  )
}
